import React, { useState, useEffect } from 'react';
import { useTitle } from '../../../context/TitleContext';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaArrowLeft, FaEdit, FaTrash, FaHeadset, FaQuestionCircle, FaReply } from 'react-icons/fa';
import { showSuccessToast, showErrorToast } from '../../../shared/utils/toastHelper';
import ConfirmModal from '../../../shared/components/ConfirmModal';
import apiClient from '../../../shared/utils/apiConfig';

const AdminSupportDetails = () => {
    const { setTitle } = useTitle();
    const { questionId } = useParams();
    const navigate = useNavigate();
    
    const [question, setQuestion] = useState(null);
    const [loading, setLoading] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);

    useEffect(() => {
        setTitle('تفاصيل سؤال الدعم');
    }, [setTitle]);

    useEffect(() => {
        fetchQuestionDetails();
    }, [questionId]);

    const fetchQuestionDetails = async () => {
        try {
            setLoading(true);
            
            const response = await apiClient.get(`/admin/support-questions/${questionId}`);
            
          
            
            if (response.data.status === 'success' || response.data.status === 200 || response.data.statusCode === 200) {
                // البيانات تأتي في response.data.data
                const questionData = response.data.data || response.data;
                setQuestion(questionData);
            } else {
                throw new Error(response.data.message || 'فشل في جلب تفاصيل السؤال');
            }
        } catch (error) {
            showErrorToast(error.response?.data?.message || error.message || 'فشل في جلب تفاصيل السؤال');
            navigate('/admin-support');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        try {
            const response = await apiClient.delete(`/admin/support-questions/${questionId}`);
            
            // التحقق من نجاح العملية
            if (response.data.status === 'success' || response.data.status === 200 || response.data.statusCode === 200) {
                showSuccessToast('تم حذف السؤال بنجاح');
                // الانتظار قليلاً لإظهار التوست قبل التنقل
                setTimeout(() => {
                    navigate('/admin-support');
                }, 1000);
            } else {
                throw new Error(response.data.message || 'فشل في حذف السؤال');
            }
        } catch (error) {
            showErrorToast(error.response?.data?.message || error.message || 'فشل في حذف السؤال');
        }
    };

    const handleEdit = () => {
        navigate(`/admin-support/edit/${questionId}`);
    };

    const confirmDelete = () => {
        setShowDeleteModal(true);
    };

    const executeDelete = () => {
        handleDelete();
        setShowDeleteModal(false);
    };

    if (loading) {
        return (
            <div className="main_content">
                <div className="dashboard-container">
                    <div className="text-center py-5">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                        <p className="mt-3 text-muted">جاري تحميل تفاصيل السؤال...</p>
                    </div>
                </div>
            </div>
        );
    }

    if (!question) {
        return (
            <div className="main_content">
                <div className="dashboard-container">
                    <div className="text-center py-5">
                        <FaQuestionCircle className="text-muted" style={{ fontSize: '3rem' }} />
                        <p className="mt-3 text-muted">السؤال غير موجود</p>
                        <button 
                            className="btn btn-primary"
                            onClick={() => navigate('/admin-support')}>
                            العودة للقائمة
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="main_content">
            <div className="dashboard-container">
                {/* Header */}
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <div className="d-flex align-items-center">
                        <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            className="btn btn-outline-secondary mx-3"
                            onClick={() => navigate('/admin-support')}>
                            <FaArrowLeft className="mx-2" />
                            العودة
                        </motion.button>
                        <h4 className="mb-0">تفاصيل سؤال الدعم</h4>
                    </div>
                    <div className="d-flex gap-2">
                        <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            className="btn btn-warning"
                            onClick={handleEdit}>
                            <FaEdit className="mx-2" />
                            تعديل
                        </motion.button>
                        <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            className="btn btn-danger"
                            onClick={confirmDelete}>
                            <FaTrash className="mx-2" />
                            حذف
                        </motion.button>
                    </div>
                </div>

                {/* Question Details */}
                <div className="row">
                    <div className="col-md-6 mb-4">
                        <motion.div
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.5 }}
                            className="card h-100">
                            <div className="card-header bg-primary text-white">
                                <h5 className="mb-0">
                                    <FaQuestionCircle className="mx-2" />
                                    السؤال (عربي)
                                </h5>
                            </div>
                            <div className="card-body">
                                <p className="card-text">{question.question_ar || 'غير محدد'}</p>
                            </div>
                        </motion.div>
                    </div>
                    <div className="col-md-6 mb-4">
                        <motion.div
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.5, delay: 0.1 }}
                            className="card h-100">
                            <div className="card-header bg-info text-white">
                                <h5 className="mb-0">
                                    <FaQuestionCircle className="mx-2" />
                                    السؤال (إنجليزي)
                                </h5>
                            </div>
                            <div className="card-body">
                                <p className="card-text">{question.question_en || 'غير محدد'}</p>
                            </div>
                        </motion.div>
                    </div>
                </div>

                <div className="row">
                    <div className="col-md-6 mb-4">
                        <motion.div
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.5, delay: 0.2 }}
                            className="card h-100">
                            <div className="card-header bg-success text-white">
                                <h5 className="mb-0">
                                    <FaReply className="mx-2" />
                                    الإجابة (عربي)
                                </h5>
                            </div>
                            <div className="card-body">
                                <p className="card-text">{question.answer_ar || 'غير محدد'}</p>
                            </div>
                        </motion.div>
                    </div>
                    <div className="col-md-6 mb-4">
                        <motion.div
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.5, delay: 0.3 }}
                            className="card h-100">
                            <div className="card-header bg-warning text-white">
                                <h5 className="mb-0">
                                    <FaReply className="mx-2" />
                                    الإجابة (إنجليزي)
                                </h5>
                            </div>
                            <div className="card-body">
                                <p className="card-text">{question.answer_en || 'غير محدد'}</p>
                            </div>
                        </motion.div>
                    </div>
                </div>

                {/* Delete Confirmation Modal */}
                <ConfirmModal
                    show={showDeleteModal}
                    onHide={() => setShowDeleteModal(false)}
                    onConfirm={executeDelete}
                    title="تأكيد الحذف"
                    message={`هل أنت متأكد من حذف السؤال: "${question.question_ar || 'غير محدد'}"؟`}
                    confirmText="حذف"
                    cancelText="إلغاء"
                    variant="danger"
                />
            </div>
        </div>
    );
};

export default AdminSupportDetails;
